import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, UsersRound } from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";
import {
  CONVERTED_STATUSES,
  LEAD_SELECT_COLUMNS,
  formatAffiliateDate,
  normalizeAffiliateLeadStatus,
} from "../lib/salesAffiliateCommercial";

function transactionLabel(value) {
  if (value === "ONE_TIME") return "One-time plan";
  if (value === "SUBSCRIPTION") return "Recurring subscription";
  return "Paid plan";
}

export default function SalesAffiliateSubscribers() {
  const { loading, workspaceData: status, configuration, lifecycleState } =
    useSalesAffiliateWorkspace();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (loading || !configuration.allowsDashboardPage) return;

    let alive = true;
    (async () => {
      setBusy(true);
      setError("");

      // Canonical commercial-attribution provenance is intentionally
      // service-role-only. The authenticated Affiliate read surface is the
      // Affiliate-owned lead row after the certified attribution workflow
      // converts it to CONVERTED and records converted_at / transaction type.
      const { data, error: loadError } = await supabase
        .from("sales_affiliate_leads")
        .select(LEAD_SELECT_COLUMNS)
        .order("converted_at", { ascending: false, nullsFirst: false });

      if (!alive) return;

      if (loadError) {
        setError("Unable to load attributed customers right now. Please try again.");
        setRows([]);
      } else {
        setRows(
          (data || []).filter((row) =>
            CONVERTED_STATUSES.has(normalizeAffiliateLeadStatus(row.status))
          )
        );
      }
      setBusy(false);
    })();

    return () => {
      alive = false;
    };
  }, [loading, configuration.allowsDashboardPage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        !q ||
        [row.company_name, row.first_name, row.last_name, row.email].some(
          (value) => String(value || "").toLowerCase().includes(q)
        )
    );
  }, [rows, query]);

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="subscribers">
        <div className="p-10">Loading attributed customers…</div>
      </SalesAffiliateLayout>
    );
  }

  if (!configuration.allowsDashboardPage || !status?.affiliate_exists) {
    return <Navigate to={configuration.defaultPath} replace />;
  }

  return (
    <SalesAffiliateLayout
      activeSection="subscribers"
      lifecycleState={lifecycleState}
    >
      <main className="mx-auto max-w-7xl px-6 py-8 sm:px-8">
        <div className="text-[11px] font-black uppercase tracking-[0.24em] text-teal-700">
          Attributed Customer Portfolio
        </div>
        <h1 className="mt-2 text-4xl font-black text-slate-950">
          Attributed Customers
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          View Protected Prospects that converted to qualifying paid AI Fine
          Protector plans and were attributed to you.
        </p>

        <section className="mt-7 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-emerald-800">
              Attributed Customers
            </div>
            <div className="mt-3 text-3xl font-black">{rows.length}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
              Attribution
            </div>
            <div className="mt-3 text-sm font-bold text-slate-700">
              Recorded at conversion
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
              Transaction Type
            </div>
            <div className="mt-3 text-sm font-bold text-slate-700">
              Subscription or one-time
            </div>
          </div>
        </section>

        <section className="mt-7 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              aria-label="Search attributed customers"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search attributed customers…"
              className="w-full rounded-2xl border border-slate-300 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-6 overflow-x-auto">
            {busy ? (
              <div className="rounded-2xl bg-slate-50 p-8 text-center">
                Loading attributed customers…
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-12 text-center">
                <UsersRound className="mx-auto text-teal-600" size={30} />
                <div className="mt-3 font-black">No attributed customers yet</div>
                <p className="mt-2 text-sm text-slate-600">
                  When a protected prospect becomes a paid AI Fine Protector
                  customer and attribution is finalized, the converted record
                  will appear here.
                </p>
              </div>
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-3 py-3">Customer / Company</th>
                    <th className="px-3 py-3">Transaction</th>
                    <th className="px-3 py-3">Attributed On</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="px-3 py-4">
                        <div className="font-black text-slate-950">
                          {row.company_name || "Company unavailable"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {[row.first_name, row.last_name]
                            .filter(Boolean)
                            .join(" ") || row.email}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-slate-600">
                        {transactionLabel(row.converted_transaction_type)}
                      </td>
                      <td className="px-3 py-4 text-slate-600">
                        {formatAffiliateDate(row.converted_at)}
                      </td>
                      <td className="px-3 py-4 font-bold text-emerald-700">
                        {row.converted_transaction_type === "SUBSCRIPTION"
                          ? "Subscriber"
                          : row.converted_transaction_type === "ONE_TIME"
                            ? "One-time Customer"
                            : "Attributed Customer"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </SalesAffiliateLayout>
  );
}
